import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { can } from "@/lib/helpers";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Barcode as BarcodeSvg } from "@/components/Barcode";
import { Plus, MagnifyingGlass, Barcode, ArrowsClockwise, Printer } from "@phosphor-icons/react";

const STATUSES = ["Available", "Reserved", "Sold", "Returned", "In Transit", "Damaged"];

export default function Inventory() {
  const { user, branches, activeBranch } = useAuth();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stockOpen, setStockOpen] = useState(false);
  const [adjItem, setAdjItem] = useState(null);
  const [labelItem, setLabelItem] = useState(null);
  const [stockForm, setStockForm] = useState({ product_id: "", quantity: 1, rack: "", tray: "" });
  const [adjForm, setAdjForm] = useState({ status: "", rack: "", tray: "", reason: "" });
  const canStockIn = can(user, "inventory", "create");
  const canAdjust = can(user, "inventory", "stock_adjust");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    if (activeBranch) params.set("branch_id", activeBranch);
    setRows((await api.get(`/inventory?${params}`)).data);
  }, [search, statusFilter, activeBranch]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)); }, []);

  const doStockIn = async () => {
    try {
      await api.post("/inventory/stock-in", { ...stockForm, quantity: Number(stockForm.quantity), branch_id: activeBranch });
      toast.success("Stock added & tagged");
      setStockOpen(false); setStockForm({ product_id: "", quantity: 1, rack: "", tray: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doAdjust = async () => {
    try {
      const payload = {};
      ["status", "rack", "tray"].forEach((k) => { if (adjForm[k]) payload[k] = adjForm[k]; });
      payload.reason = adjForm.reason;
      await api.put(`/inventory/${adjItem.id}/adjust`, payload);
      toast.success("Inventory adjusted");
      setAdjItem(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Inventory" subtitle="Per-item tracking, status lifecycle, location & barcode tags.">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Scan / search barcode…" className="pl-9 w-56 bg-white border-[#E6E2D6]" data-testid="inventory-search" />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px] bg-white border-[#E6E2D6]" data-testid="inventory-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {canStockIn && <Button onClick={() => setStockOpen(true)} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="stock-in-btn"><Plus size={16} className="mr-1" /> Stock In</Button>}
      </PageHeader>

      <Card className="bg-white border-[#E6E2D6] shadow-sm">
        {rows.length === 0 ? <EmptyState label="No inventory items" /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Barcode</TableHead><TableHead>Product</TableHead><TableHead>Purity</TableHead>
                  <TableHead className="text-right">Net Wt</TableHead><TableHead>Location</TableHead>
                  <TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((i) => (
                  <TableRow key={i.id} data-testid={`inv-row-${i.barcode}`}>
                    <TableCell className="font-mono text-xs">{i.barcode}</TableCell>
                    <TableCell>{i.product_name}</TableCell>
                    <TableCell>{i.purity}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(i.net_weight).toFixed(3)}</TableCell>
                    <TableCell className="text-sm">{i.rack || "-"}/{i.tray || "-"}</TableCell>
                    <TableCell><StatusBadge status={i.status} /></TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setLabelItem(i)} title="Label" data-testid={`label-${i.barcode}`}><Barcode size={16} /></Button>
                      {canAdjust && <Button variant="ghost" size="icon" onClick={() => { setAdjItem(i); setAdjForm({ status: i.status, rack: i.rack || "", tray: i.tray || "", reason: "" }); }} title="Adjust" data-testid={`adjust-${i.barcode}`}><ArrowsClockwise size={16} /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Stock In */}
      <Dialog open={stockOpen} onOpenChange={setStockOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Stock In</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Product</Label>
              <Select value={stockForm.product_id} onValueChange={(v) => setStockForm({ ...stockForm, product_id: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="stock-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-[#6B6B6B]">Quantity</Label><Input type="number" min="1" value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="stock-qty" /></div>
              <div><Label className="text-xs text-[#6B6B6B]">Rack</Label><Input value={stockForm.rack} onChange={(e) => setStockForm({ ...stockForm, rack: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="stock-rack" /></div>
              <div><Label className="text-xs text-[#6B6B6B]">Tray</Label><Input value={stockForm.tray} onChange={(e) => setStockForm({ ...stockForm, tray: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="stock-tray" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={doStockIn} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="stock-save">Add Stock</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust */}
      <Dialog open={!!adjItem} onOpenChange={(o) => !o && setAdjItem(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Adjust Item</DialogTitle></DialogHeader>
          {adjItem && <div className="text-xs text-[#8A8A8A] mb-2 font-mono">{adjItem.barcode} · {adjItem.product_name}</div>}
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Status</Label>
              <Select value={adjForm.status} onValueChange={(v) => setAdjForm({ ...adjForm, status: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="adj-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B6B6B]">Rack</Label><Input value={adjForm.rack} onChange={(e) => setAdjForm({ ...adjForm, rack: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="adj-rack" /></div>
              <div><Label className="text-xs text-[#6B6B6B]">Tray</Label><Input value={adjForm.tray} onChange={(e) => setAdjForm({ ...adjForm, tray: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="adj-tray" /></div>
            </div>
            <div><Label className="text-xs text-[#6B6B6B]">Reason</Label><Input value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="Reason for adjustment" className="mt-1 bg-white border-[#E6E2D6]" data-testid="adj-reason" /></div>
          </div>
          <DialogFooter><Button onClick={doAdjust} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="adj-save">Save Adjustment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Barcode Label */}
      <Dialog open={!!labelItem} onOpenChange={(o) => !o && setLabelItem(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Product Tag</DialogTitle></DialogHeader>
          {labelItem && (
            <div id="label-print" className="border border-dashed border-[#C5A059] rounded-md p-4 text-center">
              <div className="font-display text-lg text-[#C5A059] font-semibold">OM GOLD</div>
              <div className="text-sm font-medium mt-1">{labelItem.product_name}</div>
              <div className="text-xs text-[#8A8A8A]">{labelItem.purity} · {Number(labelItem.net_weight).toFixed(3)}g</div>
              <div className="mt-2"><BarcodeSvg value={labelItem.barcode} testid="tag-barcode" /></div>
              <div className="text-[10px] text-[#8A8A8A] mt-1">{labelItem.sku}</div>
            </div>
          )}
          <DialogFooter><Button onClick={() => window.print()} variant="outline" className="border-[#E6E2D6]" data-testid="print-label"><Printer size={16} className="mr-1" /> Print</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
